import Vue from 'vue'
import bossesAPI from '@/api/sorority-wars/bossesAPI'

const emptyProgress = () => ({
  damage_dealt: 0,
  attacks_won: 0,
  attacks_lost: 0
})

const normalizeErrors = errors => {
  if (Array.isArray(errors)) {
    return errors.reduce((all, error) => all.concat(normalizeErrors(error)), [])
  }
  if (errors && typeof errors === 'object') {
    return Object.keys(errors).reduce((all, key) => all.concat(normalizeErrors(errors[key])), [])
  }
  return errors ? [errors] : []
}

const responseErrors = error => {
  if (error.response && error.response.data) {
    if (error.response.data.errors) return normalizeErrors(error.response.data.errors)
    if (error.response.data.error) return normalizeErrors(error.response.data.error)
  }

  return ['Unable to complete the boss request.']
}

const state = {
  bosses: [],
  serverTime: null,
  rewards: [],
  progress: emptyProgress(),
  loading: false,
  rewardsLoading: false,
  attacking: false,
  claimLoading: {},
  errors: [],
  rewardErrors: [],
  attackErrors: [],
  claimErrors: {},
  lastAttack: null,
  newlyUnlockedRewardIds: []
}

const mutations = {
  setBosses (state, bosses) {
    state.bosses = bosses || []
  },
  setServerTime (state, serverTime) {
    state.serverTime = serverTime || null
  },
  setRewards (state, rewards) {
    state.rewards = rewards || []
  },
  setProgress (state, progress) {
    state.progress = { ...emptyProgress(), ...(progress || {}) }
  },
  setLoading (state, loading) {
    state.loading = loading
  },
  setRewardsLoading (state, loading) {
    state.rewardsLoading = loading
  },
  setAttacking (state, attacking) {
    state.attacking = attacking
  },
  setClaimLoading (state, { rewardId, loading }) {
    if (loading) Vue.set(state.claimLoading, rewardId, true)
    else Vue.delete(state.claimLoading, rewardId)
  },
  setErrors (state, errors) {
    state.errors = errors || []
  },
  setRewardErrors (state, errors) {
    state.rewardErrors = errors || []
  },
  setAttackErrors (state, errors) {
    state.attackErrors = errors || []
  },
  setClaimErrors (state, { rewardId, errors }) {
    if (errors && errors.length) Vue.set(state.claimErrors, rewardId, errors)
    else Vue.delete(state.claimErrors, rewardId)
  },
  setLastAttack (state, attack) {
    state.lastAttack = attack || null
  },
  setNewlyUnlockedRewardIds (state, ids) {
    state.newlyUnlockedRewardIds = ids || []
  },
  updateBoss (state, boss) {
    if (!boss || typeof boss.id === 'undefined') return

    state.bosses = state.bosses.map(item => {
      return String(item.id) === String(boss.id) ? { ...item, ...boss } : item
    })
  },
  updateReward (state, reward) {
    if (!reward || typeof reward.id === 'undefined') return

    state.rewards = state.rewards.map(item => {
      return String(item.id) === String(reward.id) ? { ...item, ...reward } : item
    })
  },
  clearDetail (state) {
    state.rewards = []
    state.progress = emptyProgress()
    state.rewardErrors = []
    state.attackErrors = []
    state.claimErrors = {}
    state.lastAttack = null
    state.newlyUnlockedRewardIds = []
  }
}

const actions = {
  getBosses ({ commit }, options = {}) {
    const silent = options.silent === true
    if (!silent) commit('setLoading', true)
    if (!silent) commit('setErrors', [])

    return bossesAPI.getAll()
      .then(res => {
        if (res.data.success) {
          commit('setBosses', res.data.bosses)
          commit('setServerTime', res.data.server_time)
        } else if (!silent) {
          commit('setErrors', normalizeErrors(res.data.errors || ['Unable to load bosses.']))
        }

        return res.data
      })
      .catch(error => {
        const errors = responseErrors(error)
        if (!silent) commit('setErrors', errors)
        return { success: false, errors }
      })
      .finally(() => {
        if (!silent) commit('setLoading', false)
      })
  },
  getRewards ({ commit }, bossId) {
    commit('setRewardsLoading', true)
    commit('setRewardErrors', [])

    return bossesAPI.getRewards(bossId)
      .then(res => {
        if (res.data.success) {
          commit('setRewards', res.data.rewards)
          commit('setProgress', res.data.progress)
        } else {
          commit('setRewards', [])
          commit('setProgress', null)
          commit('setRewardErrors', normalizeErrors(res.data.errors || ['Unable to load boss rewards.']))
        }

        return res.data
      })
      .catch(error => {
        const errors = responseErrors(error)
        commit('setRewards', [])
        commit('setProgress', null)
        commit('setRewardErrors', errors)
        return { success: false, errors }
      })
      .finally(() => commit('setRewardsLoading', false))
  },
  attackBoss ({ commit, dispatch }, { bossId, captchaResponse }) {
    commit('setAttacking', true)
    commit('setAttackErrors', [])
    commit('setLastAttack', null)
    commit('setNewlyUnlockedRewardIds', [])

    return bossesAPI.attack(bossId, captchaResponse)
      .then(res => {
        if (!res.data.success) {
          const errors = normalizeErrors(res.data.errors || ['Unable to attack this boss.'])
          commit('setAttackErrors', errors)
          return res.data
        }

        const results = res.data.results || {}
        const unlockedIds = results.newly_unlocked_reward_ids || res.data.newly_unlocked_reward_ids || []
        commit('setLastAttack', results)
        commit('setNewlyUnlockedRewardIds', unlockedIds)
        commit('updateBoss', res.data.boss || results.boss)
        commit('setProgress', res.data.progress || results.progress)

        return Promise.all([
          dispatch('getBosses', { silent: true }),
          dispatch('getRewards', bossId)
        ]).then(() => res.data)
      })
      .catch(error => {
        const errors = responseErrors(error)
        commit('setAttackErrors', errors)
        return { success: false, errors }
      })
      .finally(() => commit('setAttacking', false))
  },
  claimReward ({ commit, dispatch }, { bossId, rewardId, captchaResponse }) {
    commit('setClaimLoading', { rewardId, loading: true })
    commit('setClaimErrors', { rewardId, errors: [] })

    return bossesAPI.claimReward(bossId, rewardId, captchaResponse)
      .then(res => {
        if (!res.data.success) {
          commit('setClaimErrors', {
            rewardId,
            errors: normalizeErrors(res.data.errors || ['Unable to claim this reward.'])
          })
          return res.data
        }

        commit('updateReward', res.data.reward)
        return Promise.all([
          dispatch('getBosses', { silent: true }),
          dispatch('getRewards', bossId)
        ]).then(() => res.data)
      })
      .catch(error => {
        const errors = responseErrors(error)
        commit('setClaimErrors', { rewardId, errors })
        return { success: false, errors }
      })
      .finally(() => commit('setClaimLoading', { rewardId, loading: false }))
  },
  clearDetail ({ commit }) {
    commit('clearDetail')
  }
}

const getters = {
  bossById: state => bossId => {
    return state.bosses.find(boss => String(boss.id) === String(bossId)) || null
  }
}

export default {
  state,
  mutations,
  actions,
  getters,
  namespaced: true
}
